import { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import keishIcon from "@/assets/icon-green.svg";
import keishIconWhite from "@/assets/icon-white.svg";
import ContactSheet from "@/components/ContactSheet";
import FooterSection from "@/components/FooterSection";
import { useTheme } from "@/components/ThemeProvider";
import SEO from "@/components/SEO";
import {
  templates,
  allCategories,
  allPlatforms,
  priceRanges,
  type ShopTemplate,
} from "@/data/shopTemplates";

/* ─── Filter dropdown (matches /work page) ─── */
const FilterDropdown = ({
  value,
  onChange,
  options,
  labelMap,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  labelMap: (v: string) => string;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`font-body text-ts-2xs tracking-[0.1em] uppercase px-2.5 py-1.5 border transition-all duration-300 flex items-center gap-2 ${
          value !== "All"
            ? "bg-foreground text-background border-foreground"
            : "border-border text-muted-foreground"
        }`}
      >
        {labelMap(value)}
        <svg
          className={`w-2 h-2 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 10 6"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="m1 1 4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-background border border-border shadow-lg min-w-[200px] max-h-[260px] overflow-y-auto">
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full text-left px-3 py-2 font-body text-ts-2xs tracking-wide transition-colors ${
                value === opt
                  ? "bg-foreground text-background font-medium"
                  : "text-foreground hover:bg-muted"
              }`}
            >
              {labelMap(opt)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Template Card                                                     */
/* ------------------------------------------------------------------ */
const TemplateCard = ({ template }: { template: ShopTemplate }) => {
  const [imgIdx, setImgIdx] = useState(0);
  const count = template.images.length;

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.4 }}
      className="group flex flex-col"
    >
      {/* Image */}
      <div className="relative overflow-hidden rounded-lg mb-4 bg-muted dark:bg-foreground/[0.06] ring-1 ring-border/50 dark:ring-foreground/[0.08]">
        <img
          src={template.images[imgIdx]}
          alt={`${template.title} preview`}
          className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-[1.02]"
        />
        {/* Desktop hover arrows */}
        {count > 1 && (
          <>
            <button
              onClick={() => setImgIdx((imgIdx - 1 + count) % count)}
              aria-label="Previous image"
              className="hidden md:flex absolute left-2.5 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-background/70 backdrop-blur-sm text-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-background/90"
            >
              <ChevronLeft size={15} />
            </button>
            <button
              onClick={() => setImgIdx((imgIdx + 1) % count)}
              aria-label="Next image"
              className="hidden md:flex absolute right-2.5 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-background/70 backdrop-blur-sm text-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-background/90"
            >
              <ChevronRight size={15} />
            </button>
          </>
        )}
        {/* Thumbnail dots */}
        {count > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {template.images.map((_, i) => (
              <button
                key={i}
                onClick={() => setImgIdx(i)}
                aria-label={`View image ${i + 1}`}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  i === imgIdx
                    ? "bg-white scale-125"
                    : "bg-white/50 hover:bg-white/80"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Meta */}
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <h2 className="font-display text-lg md:text-xl italic text-foreground leading-tight">
          {template.title}
        </h2>
        <span className="font-heading text-lg font-bold text-foreground flex-shrink-0">
          {template.priceDisplay}
        </span>
      </div>

      <p className="font-body text-ts-2xs text-muted-foreground mb-1">
        {template.subtitle}
      </p>

      <p className="font-body text-ts-2xs text-muted-foreground/70 leading-relaxed line-clamp-2 mb-4">
        {template.description}
      </p>

      {/* Features */}
      <ul className="mb-5 grid grid-cols-1 gap-y-1">
        {template.features.slice(0, 4).map((f) => (
          <li
            key={f}
            className="font-body text-[11px] text-muted-foreground flex items-center gap-2"
          >
            <span className="w-1 h-1 rounded-full bg-foreground/20 flex-shrink-0" />
            {f}
          </li>
        ))}
      </ul>

      {/* Actions */}
      <div className="mt-auto flex flex-col gap-2">
        <a
          href={template.purchaseUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground font-body text-ts-2xs font-medium tracking-[0.15em] uppercase transition-colors hover:bg-primary/90 rounded"
        >
          Buy Template
          <ArrowUpRight size={11} />
        </a>

        <div className="flex items-center gap-4 justify-center">
          <a
            href={template.livePreviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-body text-[11px] tracking-[0.08em] uppercase text-muted-foreground hover:text-foreground transition-colors"
          >
            Preview
            <ExternalLink size={9} />
          </a>
          <Link
            to={template.caseStudyUrl || `/work/${template.slug}`}
            className="inline-flex items-center gap-1 font-body text-[11px] tracking-[0.08em] uppercase text-muted-foreground hover:text-foreground transition-colors"
          >
            Case Study
            <ArrowUpRight size={9} />
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */
const Shop = () => {
  const { mode } = useTheme();
  const isDark = mode === "dark";

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [selectedPriceIdx, setSelectedPriceIdx] = useState<number | null>(null);

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      if (selectedCategory && t.category !== selectedCategory) return false;
      if (selectedPlatform && t.platform !== selectedPlatform) return false;
      if (selectedPriceIdx !== null) {
        const range = priceRanges[selectedPriceIdx];
        if (t.price < range.min || t.price > range.max) return false;
      }
      return true;
    });
  }, [selectedCategory, selectedPlatform, selectedPriceIdx]);

  const hasFilters =
    selectedCategory !== null ||
    selectedPlatform !== null ||
    selectedPriceIdx !== null;

  const clearAll = () => {
    setSelectedCategory(null);
    setSelectedPlatform(null);
    setSelectedPriceIdx(null);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="Shop Templates"
        description="Premium Framer templates by KEISH. Production-ready, CMS-powered, and built with editorial precision."
        canonical="/shop"
      />

      {/* Header */}
      <header
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-5 md:px-10 lg:px-14 py-4 backdrop-blur-xl border-b transition-colors duration-500"
        style={{
          backgroundColor: isDark ? "hsla(0, 0%, 9%, 0.9)" : "hsla(45, 100%, 98%, 0.9)",
          color: isDark ? "hsl(27, 61%, 92%)" : "hsl(0, 0%, 9%)",
          borderColor: isDark ? "hsla(27, 61%, 92%, 0.1)" : "hsl(45, 10%, 88%)",
        }}
      >
        <Link to="/" className="flex items-center gap-2 group">
          <ArrowLeft size={14} className="opacity-50 group-hover:opacity-100 transition-opacity" />
          <img src={isDark ? keishIconWhite : keishIcon} alt="KEISH" className="w-6 h-6" />
          <span className="font-body text-ts-2xs font-semibold tracking-[0.2em] uppercase">
            KEISH
          </span>
        </Link>
        <ContactSheet>
          <button className="font-body text-ts-2xs tracking-[0.15em] uppercase opacity-50 hover:opacity-100 transition-opacity">
            Work With Us
          </button>
        </ContactSheet>
      </header>

      {/* Hero */}
      <div className="pt-28 md:pt-32">
        <div className="px-5 md:px-10 lg:px-14 pb-8 md:pb-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
          >
            <p className="font-body text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground mb-3">
              Shop
            </p>
            <h1 className="font-display text-4xl md:text-6xl italic text-foreground mb-3">
              Templates
            </h1>
            <p className="font-body text-ts-sm text-muted-foreground tracking-wide max-w-[520px]">
              Production-ready templates built with the same structure and attention we bring to
              client work. Plug in your brand and launch.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-5 md:px-10 lg:px-14 pb-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="flex flex-wrap items-center gap-2"
        >
          <FilterDropdown
            value={selectedCategory ?? "All"}
            onChange={(v) => setSelectedCategory(v === "All" ? null : v)}
            options={["All", ...allCategories]}
            labelMap={(v) => v === "All" ? "Industry" : v}
          />
          <FilterDropdown
            value={selectedPlatform ?? "All"}
            onChange={(v) => setSelectedPlatform(v === "All" ? null : v)}
            options={["All", ...allPlatforms]}
            labelMap={(v) => v === "All" ? "Platform" : v}
          />
          <FilterDropdown
            value={selectedPriceIdx !== null ? priceRanges[selectedPriceIdx].label : "All"}
            onChange={(v) => {
              if (v === "All") { setSelectedPriceIdx(null); return; }
              const idx = priceRanges.findIndex((r) => r.label === v);
              setSelectedPriceIdx(idx >= 0 ? idx : null);
            }}
            options={["All", ...priceRanges.map((r) => r.label)]}
            labelMap={(v) => v === "All" ? "Price" : v}
          />
          {hasFilters && (
            <button
              onClick={clearAll}
              className="font-body text-[11px] tracking-[0.1em] uppercase text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2 ml-1"
            >
              Clear
            </button>
          )}
        </motion.div>
      </div>

      {/* Grid */}
      <div className="px-5 md:px-10 lg:px-14 pb-24 flex-1">
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {filtered.map((template) => (
              <TemplateCard key={template.slug} template={template} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="font-body text-ts-sm text-muted-foreground">
              No templates match your filters.
            </p>
            <button
              onClick={clearAll}
              className="mt-3 font-body text-ts-2xs tracking-[0.1em] uppercase text-foreground underline underline-offset-2"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      <FooterSection />
    </div>
  );
};

export default Shop;
